import { defineComponent as _defineComponent } from 'vue'
import GoToService from '@/router/GoToService';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { usePreloadedIframe } from '@/composables/usePreloadedIframe';
import AuthService from '@/modules/user/AuthService';
import PreloadedIframeRiveLoader from '@/components/PreloadedIframeRiveLoader.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'PreloadedV3Iframe',
  setup(__props) {

const iframeRef = ref<HTMLIFrameElement | null>(null);
const iframeSrc = ref('');

const {
  isVisible,
  isFullscreen,
  isContentLoading,
  currentModalName,
  modalOptions,
  setIframeElement,
  closeModal,
  sendPing,
} = usePreloadedIframe();

// Drives F2's type-aware resolver: present (= the open modal's type) only while
// visible; cleared when parked so a parked iframe never steals legacy messages.
const activeModalType = computed(() =>
  isVisible.value ? currentModalName.value ?? undefined : undefined,
);

const isPersistent = computed(() => modalOptions.value.persistent !== false);
const isFullscreenActive = computed(
  () => isVisible.value && (isFullscreen.value || !!modalOptions.value.fullscreen),
);
const modalClass = computed(() => [
  'preloaded-modal',
  isVisible.value ? 'preloaded-modal--open' : 'preloaded-modal--parked',
  modalOptions.value.contentClass || '',
  { 'iframe-modal-fullscreen': isFullscreenActive.value },
]);
const modalStyle = computed(() => {
  if (!isVisible.value || isFullscreenActive.value) {
    return {};
  }
  const width = modalOptions.value.maxWidth;
  const maxWidth = typeof width === 'number' ? `${width}px` : width;
  return { maxWidth };
});

const getPreloadedIframeSrc = (withCacheBuster = false) => {
  const baseUrl = GoToService.getLendiV3Url('iframe-host', true);
  if (!withCacheBuster) {
    return baseUrl;
  }
  try {
    const url = new URL(baseUrl);
    url.searchParams.set('ts', String(Date.now()));
    return url.toString();
  } catch {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}ts=${Date.now()}`;
  }
};

const scheduleIframeLoad = () => {
  const setSrc = () => {
    iframeSrc.value = getPreloadedIframeSrc();
  };
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(setSrc, { timeout: 1000 });
  } else {
    setTimeout(setSrc, 1000);
  }
};

const prewarmIframeHost = () => {
  try {
    const url = new URL(GoToService.getLendiV3Url('iframe-host', true));
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = url.origin;
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);
  } catch (error) {
    console.error('[PreloadedV3Iframe] prewarm failed', error);
  }
};

const closeIfAllowed = () => {
  if (!isPersistent.value) {
    closeModal(null);
  }
};
const handleBackdropClick = () => closeIfAllowed();
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || !isVisible.value) return;
  closeIfAllowed();
};
const handleIframeLoad = () => sendPing();

watch(iframeRef, (value) => setIframeElement(value));

// Account switch: reload the preloaded iframe so it picks up fresh auth tokens.
watch(AuthService.userStateV2Key, (newKey, oldKey) => {
  if (newKey === oldKey) return;
  closeModal(null);
  iframeSrc.value = '';
  // A macrotask, deliberately not requestAnimationFrame: rAF never runs while
  // the tab is hidden, so an identity change in a background tab (a login or an
  // org switch, but also the ordinary boot race where the user resolves after
  // this component mounts) left iframeSrc empty for as long as the tab stayed
  // hidden. setTimeout still lands after Vue's microtask flush, so the old
  // iframe is unmounted before the new src mounts a fresh one.
  setTimeout(() => {
    iframeSrc.value = getPreloadedIframeSrc(true);
  }, 0);
});

onMounted(() => {
  prewarmIframeHost();
  scheduleIframeLoad();
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  setIframeElement(null);
});

return { __sfc: true,iframeRef, iframeSrc, isVisible, isFullscreen, isContentLoading, currentModalName, modalOptions, setIframeElement, closeModal, sendPing, activeModalType, isPersistent, isFullscreenActive, modalClass, modalStyle, getPreloadedIframeSrc, scheduleIframeLoad, prewarmIframeHost, closeIfAllowed, handleBackdropClick, handleKeydown, handleIframeLoad, PreloadedIframeRiveLoader }
}

})